import type { Transaction } from '@/types/api/payment';
import { roundMonetaryValue } from '@vaiu/utils/currency';
import { getDecimalsForCurrencyCode } from '@vaiu/utils/mmMoney';

type VolumeMap = Record<string, number>;

export interface DashboardStatsShape {
  period?: string;
  dateRange?: { start: Date | string; end: Date | string };
  overview: {
    totalTransactions: number;
    activeCurrencies: string[];
    totalUsers?: number;
    totalVolume: VolumeMap;
  };
  statistics: {
    created: { count: number; value: VolumeMap; transactions: unknown[] };
    redeemed: { count: number; value: VolumeMap; transactions: unknown[] };
    expired: { count: number; value: VolumeMap; transactions: unknown[] };
    pending: { count: number; value: VolumeMap; transactions: unknown[] };
  };
  byCurrency: Record<
    string,
    { transactions: number; volume: number; created: number; redeemed: number }
  >;
  detailedTransactions?: {
    created?: unknown[];
    redeemed?: unknown[];
    expired?: unknown[];
    pending?: unknown[];
  };
  balances?: VolumeMap;
  tokenTypes?: Record<string, { count: number; value: VolumeMap }>;
}

function parseTxAmount(tx: Transaction): number {
  const raw = tx.amount ?? tx.data?.amount;
  const n = typeof raw === 'string' ? parseFloat(raw) : Number(raw);
  return Number.isFinite(n) ? n : 0;
}

function parseTxCurrency(tx: Transaction): string {
  return (tx.currency || tx.data?.currency || 'USD').trim();
}

function parseTxDate(tx: Transaction): Date | null {
  const raw =
    tx.createdAt || tx.date || tx.created_at || tx.dateCreated || tx.updatedAt;
  if (!raw) return null;
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d;
}

function txInDateRange(
  tx: Transaction,
  start: Date,
  end: Date
): boolean {
  const d = parseTxDate(tx);
  if (!d) return false;
  return d >= start && d < end;
}

function addVolume(map: VolumeMap, currency: string, amount: number): void {
  const dec = getDecimalsForCurrencyCode(currency);
  map[currency] = roundMonetaryValue((map[currency] || 0) + amount, dec);
}

function txDedupeKey(tx: Transaction): string {
  const id = tx.id || tx._id || tx.key;
  if (id) return String(id);
  if (tx.tokenKey) return `token:${tx.tokenKey}`;
  if (tx.code) return `code:${tx.code}`;
  const at = parseTxDate(tx)?.toISOString() || '';
  return `${at}|${tx.type}|${parseTxCurrency(tx)}|${parseTxAmount(tx)}`;
}

/** API may return the same row in pending, completed, and transactions. */
export function dedupeTransactions(transactions: Transaction[]): Transaction[] {
  const seen = new Set<string>();
  const out: Transaction[] = [];
  for (const tx of transactions) {
    const k = txDedupeKey(tx);
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(tx);
  }
  return out;
}

function isCreatedTokenTransaction(tx: Transaction): boolean {
  if (tx.tokenType === 'cash' || tx.tokenType === 'invoice') return true;
  if (tx.tokenKey) return true;
  const type = (tx.type || '').toLowerCase();
  return (
    type.includes('token') &&
    (type.includes('creat') || type.includes('gener') || type.includes('issue'))
  );
}

/** One cash-token create can produce multiple ledger rows — count once per tokenKey. */
function selectCreatedTokenTransactions(txs: Transaction[]): Transaction[] {
  const matched = txs.filter(isCreatedTokenTransaction);
  const byTokenKey = new Map<string, Transaction>();
  const rest: Transaction[] = [];

  for (const tx of matched) {
    if (tx.tokenKey) {
      if (!byTokenKey.has(tx.tokenKey)) byTokenKey.set(tx.tokenKey, tx);
    } else {
      rest.push(tx);
    }
  }

  return [...byTokenKey.values(), ...dedupeTransactions(rest)];
}

function isRedeemedTokenTransaction(tx: Transaction): boolean {
  const type = (tx.type || '').toLowerCase();
  return type === 'redeem' || type === 'credit' || type === 'receive';
}

function buildByCurrencyFromTransactionLists(
  createdTx: Transaction[],
  redeemedTx: Transaction[]
): DashboardStatsShape['byCurrency'] {
  const byCurrency: DashboardStatsShape['byCurrency'] = {};

  const ensure = (currency: string) => {
    if (!byCurrency[currency]) {
      byCurrency[currency] = { transactions: 0, volume: 0, created: 0, redeemed: 0 };
    }
    return byCurrency[currency];
  };

  for (const tx of createdTx) {
    const currency = parseTxCurrency(tx);
    const row = ensure(currency);
    row.created += 1;
    row.transactions += 1;
    row.volume = roundMonetaryValue(
      row.volume + parseTxAmount(tx),
      getDecimalsForCurrencyCode(currency)
    );
  }

  for (const tx of redeemedTx) {
    const currency = parseTxCurrency(tx);
    const row = ensure(currency);
    row.redeemed += 1;
    row.transactions += 1;
    row.volume = roundMonetaryValue(
      row.volume + parseTxAmount(tx),
      getDecimalsForCurrencyCode(currency)
    );
  }

  return byCurrency;
}

/** Count per-currency tx rows from statistics transaction lists (fallback when byCurrency is sparse). */
export function countTransactionsForCurrency(
  transactions: unknown[] | undefined,
  currency: string
): number {
  if (!transactions?.length) return 0;
  return transactions.filter(
    (raw) => parseTxCurrency(raw as Transaction) === currency
  ).length;
}

function aggregateTxList(txs: unknown[]): {
  count: number;
  value: VolumeMap;
  transactions: unknown[];
} {
  const value: VolumeMap = {};
  for (const raw of txs) {
    const tx = raw as Transaction;
    addVolume(value, parseTxCurrency(tx), parseTxAmount(tx));
  }
  return { count: txs.length, value, transactions: txs };
}

/** When aggregates are empty but detailedTransactions is populated. */
export function augmentStatsFromDetailedTransactions<T extends DashboardStatsShape>(
  stats: T | null | undefined
): T | null | undefined {
  if (!stats || (stats.overview?.totalTransactions ?? 0) > 0) return stats;

  const detailed = stats.detailedTransactions;
  if (!detailed) return stats;

  const createdList = detailed.created || [];
  const redeemedList = detailed.redeemed || [];
  const expiredList = detailed.expired || [];
  const pendingList = detailed.pending || [];

  const totalDetailCount =
    createdList.length +
    redeemedList.length +
    expiredList.length +
    pendingList.length;
  if (totalDetailCount === 0) return stats;

  const created = aggregateTxList(createdList);
  const redeemed = aggregateTxList(redeemedList);
  const expired = aggregateTxList(expiredList);
  const pending = aggregateTxList(pendingList);

  const totalVolume: VolumeMap = { ...created.value };
  for (const [currency, amount] of Object.entries(redeemed.value)) {
    addVolume(totalVolume, currency, amount);
  }

  return {
    ...stats,
    overview: {
      ...stats.overview,
      totalTransactions: totalDetailCount,
      totalVolume,
      activeCurrencies: Object.keys(totalVolume),
    },
    statistics: {
      created,
      redeemed,
      expired,
      pending,
    },
    byCurrency: buildByCurrencyFromTransactionLists(
      createdList as Transaction[],
      redeemedList as Transaction[]
    ),
  };
}

/** Build dashboard stats from /transactions when get-stats aggregates are empty. */
export function mergeTransactionFallbackIntoStats<T extends DashboardStatsShape>(
  stats: T,
  transactions: Transaction[]
): T {
  if ((stats.overview?.totalTransactions ?? 0) > 0) return stats;
  if (!stats.dateRange?.start || !stats.dateRange?.end) return stats;

  const start = new Date(stats.dateRange.start);
  const end = new Date(stats.dateRange.end);
  const inRange = dedupeTransactions(transactions).filter((tx) =>
    txInDateRange(tx, start, end)
  );

  const createdTx = selectCreatedTokenTransactions(inRange);
  const redeemedTx = inRange.filter(isRedeemedTokenTransaction);

  if (createdTx.length === 0 && redeemedTx.length === 0) return stats;

  const createdValue: VolumeMap = {};
  for (const tx of createdTx) {
    addVolume(createdValue, parseTxCurrency(tx), parseTxAmount(tx));
  }

  const redeemedValue: VolumeMap = {};
  for (const tx of redeemedTx) {
    addVolume(redeemedValue, parseTxCurrency(tx), parseTxAmount(tx));
  }

  const totalVolume: VolumeMap = { ...createdValue };
  for (const [currency, amount] of Object.entries(redeemedValue)) {
    addVolume(totalVolume, currency, amount);
  }

  return {
    ...stats,
    overview: {
      ...stats.overview,
      totalTransactions: createdTx.length + redeemedTx.length,
      totalVolume,
      activeCurrencies: Object.keys(totalVolume),
    },
    statistics: {
      ...stats.statistics,
      created: {
        count: createdTx.length,
        value: createdValue,
        transactions: createdTx,
      },
      redeemed: {
        count: redeemedTx.length,
        value: redeemedValue,
        transactions: redeemedTx,
      },
    },
    byCurrency: buildByCurrencyFromTransactionLists(createdTx, redeemedTx),
  };
}

export function dashboardHasActivity(
  stats: DashboardStatsShape | null | undefined,
  currencyRowCount: number
): boolean {
  if (!stats) return false;
  return (
    (stats.overview?.totalTransactions ?? 0) > 0 ||
    (stats.statistics?.created?.count ?? 0) > 0 ||
    currencyRowCount > 0
  );
}
